import apiFetch from "../apiFetch";

/**
 * Reports a completed game session toward the student's homework assignment.
 * Call this after a game ends whenever homework params were active.
 *
 * Reads hw_params from sessionStorage (set by homeworkPage when launching a game).
 * Clears sessionStorage after reporting so the next session doesn't double-count.
 *
 * @param {number|null} resultId   - The inserted game_result / vocab_test_session id
 * @param {string}      resultType - "game" | "vocab_test"
 */
/**
 * @param {number|null} resultId    - The inserted game_result id (null for ELO games)
 * @param {string}      resultType  - "game" | "vocab_test"
 * @param {number}      correctDelta - Correct answers earned this session (default 1 for session-based games)
 */
export async function reportHomeworkCompletion(resultId, resultType = "game", correctDelta = 1) {
  const raw = sessionStorage.getItem("hw_params");
  if (!raw) return;

  let params;
  try {
    params = JSON.parse(raw);
  } catch {
    sessionStorage.removeItem("hw_params");
    return;
  }

  // Clear immediately so a second call doesn't double-report
  sessionStorage.removeItem("hw_params");

  if (!params?.assignmentId) return;

  try {
    await apiFetch(`/api/student/homework/${params.assignmentId}/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resultId: resultId ?? null, resultType, correctDelta }),
    });
  } catch {
    // Non-critical — homework tracking failure should not break the game flow
  }
}

/**
 * Returns the active homework params if a game was launched from homework, otherwise null.
 * Does NOT clear sessionStorage — call reportHomeworkCompletion to do that.
 */
export function getActiveHomeworkParams() {
  const raw = sessionStorage.getItem("hw_params");
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
